// Print a lab order requisition — the paper form that goes out with the order
// itself, before any result exists.
//
// It is not a cut-down report: what belongs on a requisition is the order, not
// its outcome. Who the patient is, who asked for the tests, when, on which
// sample, and the list of tests with their priority — everything the bench
// needs to know it has the right tube in front of it, and nothing about
// results, which have not been produced yet.
//
// One requisition covers one patient and one department: the tests picked on
// the Order Requisition Print page, however many orders they were raised
// across, print on a single slip that goes to that department's bench.
//
// Prints on the same A4 sheet and letterhead as the lab report
// (utils/reportSheet.js, utils/reportLetterhead.js) so the two documents line
// up in the patient's file.
import { toast } from "react-toastify";
import {
  buildIdBox,
  esc,
  formatAgeGender,
  renderReportSheet,
} from "../../../utils/reportSheet";
import {
  resolveFacilityDetails,
  resolveLogo,
} from "../../../utils/reportLetterhead";
import { computeAge, formatDateTime, parseOrderedTime } from "../labOrdersShared";
import { resolveLabDepartment } from "./labDepartments";
import { resolveReferringDoctor } from "./printLabReport";

const dash = (value) => {
  const s = String(value ?? "").trim();
  return s && s !== "-" ? s : "-";
};

// A row joined to the patient record carries `age`; one that only carries a
// date of birth still has an age to print.
const resolveAge = (patient) => patient?.age ?? computeAge(patient?.dob);

// Same phrasing as the Order Requisition Print screen — ward/room/bed when
// admitted, otherwise the encounter type (OPD / EMERGENCY).
const formatLocation = (patient) => {
  const ward = dash(patient?.ward || patient?.wardName);
  const room = dash(patient?.roomNo);
  const bed = dash(patient?.bedNo);
  const parts = [
    ward !== "-" ? ward : room !== "-" ? room : null,
    bed !== "-" ? `Bed ${bed}` : null,
  ].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  const patientType = dash(patient?.patientType);
  return patientType !== "-" ? patientType : "-";
};

const resolveOrderedAt = (order, patient) => {
  const candidates = [
    order?.ordered,
    order?.ordered_time,
    order?.scheduled,
    order?.sampleCreatedAt,
    order?.sampleUpdatedAt,
    patient?.appointmentStartTime,
    order?.created_at,
  ];
  for (const raw of candidates) {
    const text = String(raw ?? "").trim();
    if (!text || text === "-") continue;
    return parseOrderedTime(text) || text;
  }
  return null;
};

// The earliest test on the slip — when the doctor first asked, not print time.
const earliestOrdered = (orders, patient) =>
  (Array.isArray(orders) ? orders : [])
    .map((o) => resolveOrderedAt(o, patient))
    .filter(Boolean)
    .sort()[0] || null;

const TEST_COLUMNS = [
  "#",
  "Test / Service",
  "Department",
  "Sample",
  "Priority",
  "Order No",
  "Ordered On",
];

function buildLabOrderHtml({ patient, orders, department, printedAt }) {
  const list = Array.isArray(orders) ? orders : [];
  const primary = list[0] || {};

  const rows = list
    .map(
      (o, i) => `<tr>
        <td>${i + 1}</td>
        <td><strong>${esc(dash(o?.service))}</strong></td>
        <td>${esc(dash(resolveLabDepartment(o)))}</td>
        <td>${esc(dash(o?.sample))}</td>
        <td>${esc(dash(o?.priority))}</td>
        <td>${esc(dash(o?.orderNo))}</td>
        <td>${esc(formatDateTime(resolveOrderedAt(o, patient)))}</td>
      </tr>`,
    )
    .join("");

  return `
    <div class="rp-title">Laboratory Order Requisition</div>
    ${buildIdBox(
      [
        ["Patient Name", patient?.name],
        ["Patient ID", patient?.uhid],
        ["Age / Gender", formatAgeGender(resolveAge(patient), patient?.gender)],
        ["Patient Type", patient?.patientType],
        ["Ward / Bed", formatLocation(patient)],
        ["Contact No.", dash(patient?.phone)],
      ],
      [
        ["Department", department ? dash(department) : "All departments"],
        ["Total Tests", String(list.length)],
        ["Ordered On", formatDateTime(earliestOrdered(list, patient))],
        ["Ordering Doctor", resolveReferringDoctor(primary, patient)],
        ["Printed On", formatDateTime(printedAt || Date.now())],
      ],
    )}
    <div class="rp-body">
      <h3>Tests Ordered (${list.length})</h3>
      ${
        list.length
          ? `<table>
              <tbody>
                <tr>${TEST_COLUMNS.map((c) => `<th>${c}</th>`).join("")}</tr>
                ${rows}
              </tbody>
            </table>`
          : "<p>No tests on this requisition.</p>"
      }
    </div>
  `;
}

/**
 * Open the requisition in a viewer tab with the print dialog already asked for.
 *
 * @param {object} args
 * @param {object} args.patient    the requisition page's patient row
 * @param {Array}  args.orders     the selected tests, all for this patient
 * @param {string} args.department department the slip goes to, or "" when the
 *   selection spans every department
 * @param {Array}  args.brandingData  BrandingContext value (facility logo)
 * @param {object} args.userInfo      PermissionContext userInfo (facility address)
 */
export async function printLabOrderRequisition({
  patient,
  orders,
  department,
  printedAt,
  brandingData,
  userInfo,
}) {
  const list = Array.isArray(orders) ? orders : [];
  if (list.length === 0) {
    toast.info("Select at least one test to print.");
    return;
  }
  try {
    const { address, contact } = resolveFacilityDetails(userInfo);
    const safeName = String(patient?.name || "Patient").replace(
      /[^a-zA-Z0-9]/g,
      "_",
    );
    const safeDept = String(department || "All")
      .replace(/[^a-zA-Z0-9]/g, "_");
    const blob = await renderReportSheet({
      html: buildLabOrderHtml({ patient, orders: list, department, printedAt }),
      filename: `Lab_Requisition_${safeName}_${safeDept}.pdf`,
      autoPrint: true,
      letterhead: {
        logoBase64: resolveLogo(brandingData),
        address,
        contact,
        patientId: patient?.uhid,
        // The doctor who raised the order signs it; "Checked By" is left for
        // whoever collects the sample to counter-sign on paper.
        preparedBy: resolveReferringDoctor(list[0], patient),
        checkedBy: "",
      },
    });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      URL.revokeObjectURL(url);
      toast.error("Please allow pop-ups to print the requisition");
      return;
    }
    // Revoking immediately would pull the file out from under the viewer.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    console.error("Could not generate the lab order requisition PDF:", err);
    toast.error("Could not generate the requisition. Please try again.");
  }
}
